import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Database,
  TrendingUp,
  Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import {
  importsAPI,
  type DashboardSummary,
  type ImportJob,
  type MiddlewareQueue,
  type MiddlewareStatusResponse,
  type ImportSource,
  type ImportSourceOption,
} from '../../lib/api/imports';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';
import { scopeImportOptions, defaultImportOptionKey } from './importMonitorScope';
import JobDetailModal from './ImportJobDetailModal';
import { JobsTable } from './ImportDashboardTables';
import ImportAutoCancelledTab from './ImportAutoCancelledTab';
import ImportDateInconsistencyTab from './ImportDateInconsistencyTab';
import ImportScheduledJobsTab from './ImportScheduledJobsTab';
import ImportErpBalanceTab from './ImportErpBalanceTab';
import ImportErpReceivedTab from './ImportErpReceivedTab';
import ImportCollabHoldsTab from './ImportCollabHoldsTab';
import ImportRulesTab from './ImportRulesTab';
import ImportOrderLookupTab from './ImportOrderLookupTab';
import ImportQueueSearchTab from './ImportQueueSearchTab';
import ImportHealthCheckTab from './ImportHealthCheckTab';
import ImportWritebackTab from './ImportWritebackTab';
import { MiddlewareHealthPanel } from './MiddlewareHealthPanel';
import { DataAgeCard } from './DataAgeCard';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: React.ComponentType<{ className?: string }> }> = {
  completed: { color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle2 },
  failed: { color: 'text-red-700', bg: 'bg-red-50 border-red-200', icon: XCircle },
  partial: { color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: AlertTriangle },
  running: { color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', icon: Activity },
  pending: { color: 'text-slate-700', bg: 'bg-slate-50 border-slate-200', icon: Clock },
  cancelled: { color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200', icon: XCircle },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['pending'];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      <Icon className="h-3 w-3" />
      {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
    </span>
  );
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ImportDashboard() {
  // Writeback off (installs whose middleware never collects them) means the
  // queue can never fill, so the tab would only ever show an empty state.
  const { writebackEnabled, featureFlags } = useMenuSettings();
  // Rules & Mappings exposes handler internals and raw feed samples —
  // platform superusers only (the endpoint enforces the same gate).
  const { user } = useAuth();
  const isSuperuser = !!user?.is_superuser;
  // ERP Balance Mismatch needs the ERP's open qty, which only DE's import captures.
  const erpBalanceEnabled = user?.import_handler === 'dennis_eagle';
  // Collaboration Holds only exists where schedules are the collaboration
  // unit — accepted proposals freeze slots against ERP pushes there.
  const collabHoldsEnabled = !!featureFlags.schedule_collaboration;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Selection is keyed on the option's `key`, not its `source`. Several
  // customers can share a source (every standard tenant is source 'pna'), so
  // keying on source made those options identical — picking the second one
  // changed nothing, and every customer-scoped tab kept showing the first
  // customer's data.
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [recentJobs, setRecentJobs] = useState<ImportJob[]>([]);
  const [middleware, setMiddleware] = useState<MiddlewareStatusResponse | null>(null);
  const [dataAge, setDataAge] = useState<import('../../lib/api/imports').DataAgeResponse | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'data-age' | 'jobs' | 'writeback' | 'lookup' | 'queues' | 'cancelled' | 'date-exception' | 'new-po-date-exception' | 'scheduled-jobs' | 'erp-balance' | 'erp-received' | 'collab' | 'rules' | 'health'>('overview');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [sourceOptions, setSourceOptions] = useState<ImportSourceOption[]>([]);
  const [sourcesLoaded, setSourcesLoaded] = useState(false);
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsPageSize, setJobsPageSize] = useState(25);
  const [jobsSearchInput, setJobsSearchInput] = useState('');
  const [jobsSearch, setJobsSearch] = useState('');
  const [jobsTotalCount, setJobsTotalCount] = useState(0);
  const [jobsStatusFilter, setJobsStatusFilter] = useState<string>('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // CR-9030: the dropdown follows the top right view context.
  const {
    viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, accessibleSites, viewKey,
    isLoading: viewLoading,
  } = useView();
  // Helix operators keep the cross-tenant view: their own company has no imports.
  const unscoped = isSuperuser || user?.company_type === 'helix';
  const view = { viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, sites: accessibleSites, unscoped };
  const scopedOptions = scopeImportOptions(sourceOptions, view);
  // The view context starts on an empty default; select and fetch only once
  // it has loaded, or the first entry flashes up and a pick made meanwhile is lost.
  const ready = sourcesLoaded && !viewLoading;
  const fetchSeq = useRef(0);

  const selectedOption = scopedOptions.find(o => o.key === selectedKey);
  const source: ImportSource = selectedOption?.source ?? 'pna';
  const customerId = selectedOption?.customer_id ?? 0;
  // The top right company has no import data: say so rather than fall back
  // to the whole scope.
  const noSiteData = ready && sourceOptions.length > 0 && !selectedOption;

  const handleSearchChange = useCallback((value: string) => {
    setJobsSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setJobsSearch(value);
      setJobsPage(1);
    }, 400);
  }, []);

  // Fetch available import sources on mount
  useEffect(() => {
    importsAPI.getSources().then(setSourceOptions)
      .catch(() => {}).finally(() => setSourcesLoaded(true));
  }, []);

  // CR-9030: pre select the top right site, and follow it when it changes.
  // Keyed on viewKey so a manual pick in the dropdown is kept until then.
  useEffect(() => {
    if (!ready) return;
    setSelectedKey(defaultImportOptionKey(scopedOptions, view));
    setActiveTab('overview');
    setJobsPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, sourceOptions, viewKey]);

  const fetchData = useCallback(async (isRefresh = false) => {
    // Wait for the dropdown to settle, so the first fetch is the right customer.
    if (!ready) return;
    if (noSiteData) {
      setLoading(false);
      return;
    }
    // A reply for a customer no longer selected must not overwrite the page.
    const seq = ++fetchSeq.current;
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const jobFilters: import('../../lib/api/imports').ImportJobFilters = {
        ordering: '-created_at',
        group: 1,
        page: jobsPage,
        page_size: jobsPageSize,
        ...(jobsSearch ? { search: jobsSearch } : {}),
        ...(jobsStatusFilter ? { status: jobsStatusFilter } : {}),
        ...(customerId ? { customer: customerId } : {}),
      };

      const [dashData, jobsData, mwData, ageData] = await Promise.all([
        importsAPI.getDashboard(source, customerId || undefined),
        importsAPI.getJobs(jobFilters, source),
        source === 'pna'
          ? importsAPI.getMiddlewareStatus(source, customerId || undefined).catch(() => ({ queues: [], error: 'Redis unavailable' } as MiddlewareStatusResponse))
          : Promise.resolve({ queues: [] as MiddlewareQueue[] } as MiddlewareStatusResponse),
        // Avanti tenants only — everyone else gets {enabled:false} and no card.
        importsAPI.getDataAge().catch(() => null),
      ]);
      if (seq !== fetchSeq.current) return;

      setSummary(dashData);
      setRecentJobs(jobsData.results);
      setJobsTotalCount(jobsData.count);
      setMiddleware(mwData);
      setDataAge(ageData);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load import data';
      toast.error(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [ready, noSiteData, source, customerId, jobsPage, jobsPageSize, jobsSearch, jobsStatusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Import Monitor</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time overview of data imports from middleware and scheduled jobs
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Customer:</span>
            <select
              value={selectedKey}
              onChange={(e) => { setSelectedKey(e.target.value); setActiveTab('overview'); setJobsPage(1); setJobsSearch(''); setJobsSearchInput(''); setJobsStatusFilter(''); }}
              className="px-3 py-2 text-sm font-medium border border-border rounded-lg bg-background shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
            >
              {noSiteData && <option value="">No import data for this company</option>}
              {ready && scopedOptions.length > 0 ? (
                scopedOptions.map(opt => (
                  <option key={opt.key} value={opt.key}>{opt.customer_name}</option>
                ))
              ) : (
                <option value="">{ready ? 'No import sources in your scope' : 'Loading...'}</option>
              )}
            </select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {noSiteData ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No import data for this company. Pick a customer above, or switch company at the top right.
          </CardContent>
        </Card>
      ) : (<>
      {/* Stat Cards */}
      {summary && <StatCards summary={summary} middleware={middleware} />}

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {/* Writeback is portal -> ERP traffic, so it only exists for the
            middleware-backed sources. The CSV wizard (data_import) has no
            writeback path at all. */}
        {(source === 'data_import'
          // CR-7468: `erp-received` belongs on EVERY source, including this one.
          // It reports what the ORDER FEED said it had received, and a CSV-fed
          // customer has an order feed like any other — the tenant this work was
          // raised for is on this branch, and leaving the tab off it made the
          // page invisible exactly where it was needed (caught in the browser,
          // not by a test).
          ? (['overview', 'jobs', 'lookup', 'queues', 'cancelled', 'date-exception', 'new-po-date-exception', 'scheduled-jobs', 'erp-received', 'rules', 'health'] as const)
          : writebackEnabled
          ? (['overview', 'jobs', 'data-age', 'writeback', 'lookup', 'queues', 'cancelled', 'date-exception', 'new-po-date-exception', 'scheduled-jobs', 'erp-balance', 'erp-received', 'collab', 'rules', 'health'] as const)
          : (['overview', 'jobs', 'data-age', 'lookup', 'queues', 'cancelled', 'date-exception', 'new-po-date-exception', 'scheduled-jobs', 'erp-balance', 'erp-received', 'collab', 'rules', 'health'] as const)
        ).filter(tab => tab !== 'collab' || collabHoldsEnabled)
          .filter(tab => tab !== 'erp-balance' || erpBalanceEnabled)
          .filter(tab => tab !== 'rules' || isSuperuser)
          // CR-7468: platform operators only, gated exactly as Rules is and no
          // second gate on whether the customer's feed sends the figure — an
          // empty table is the answer, and a capability test here would drift
          // out of step with the feed (Aaron, 22 Sep 2026).
          .filter(tab => tab !== 'erp-received' || isSuperuser)
          // Avanti tenants only — the endpoint reports enabled:false elsewhere.
          .filter(tab => tab !== 'data-age' || Boolean(dataAge?.enabled))
          .map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'overview' ? 'Overview'
              : tab === 'data-age' ? 'Data Age'
              : tab === 'jobs' ? 'Recent Jobs'
              : tab === 'writeback' ? 'Writeback'
              : tab === 'lookup' ? 'Order Lookup'
              : tab === 'queues' ? 'Queue Search'
              : tab === 'cancelled' ? 'Auto-Cancelled'
              : tab === 'date-exception' ? 'Date Inconsistency Exception'
              : tab === 'new-po-date-exception' ? 'New PO Date Inconsistency Exception'
              : tab === 'scheduled-jobs' ? 'Scheduled Jobs'
              : tab === 'erp-balance' ? 'ERP Balance'
              : tab === 'erp-received' ? 'ERP Received'
              : tab === 'collab' ? 'Collaboration Holds'
              : tab === 'rules' ? 'Rules & Mappings'
              : 'Health Check'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && summary && (
        <>
          {source === 'pna' && <MiddlewareHealthPanel reports={middleware?.middleware_health} />}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <JobManagementCard jobs={recentJobs} onRefresh={() => fetchData(true)} />
            <MiddlewareStatus data={middleware} />
            <DataTypeChart data={summary.by_data_type} />
            <RecentFailures failures={summary.recent_failures} />
          </div>
        </>
      )}

      {activeTab === 'data-age' && <DataAgeCard data={dataAge} />}

      {activeTab === 'jobs' && (
        <JobsTable
          jobs={recentJobs}
          onSelectJob={setSelectedJobId}
          currentPage={jobsPage}
          totalCount={jobsTotalCount}
          pageSize={jobsPageSize}
          onPageChange={(p) => setJobsPage(p)}
          onPageSizeChange={(s) => { setJobsPageSize(s); setJobsPage(1); }}
          search={jobsSearchInput}
          onSearchChange={handleSearchChange}
          statusFilter={jobsStatusFilter}
          onStatusFilterChange={(s) => { setJobsStatusFilter(s); setJobsPage(1); }}
        />
      )}

      {/* Job Detail Modal */}
      <JobDetailModal jobId={selectedJobId} source={source} onClose={() => setSelectedJobId(null)} />

      {activeTab === 'writeback' && writebackEnabled && (
        <ImportWritebackTab
          customerId={customerId}
        />
      )}

      {activeTab === 'lookup' && <ImportOrderLookupTab customerId={customerId} />}

      {activeTab === 'queues' && <ImportQueueSearchTab customerId={customerId} />}

      {activeTab === 'cancelled' && <ImportAutoCancelledTab customerId={customerId} />}

      {/* CR-9031: the old portal's two date exception lists */}
      {activeTab === 'date-exception' && <ImportDateInconsistencyTab customerId={customerId} kind="accepted" />}
      {activeTab === 'new-po-date-exception' && <ImportDateInconsistencyTab customerId={customerId} kind="new" />}
      {/* CR-8213: run log of nightly scripts, 30 days kept */}
      {activeTab === 'scheduled-jobs' && <ImportScheduledJobsTab customerId={customerId} />}

      {activeTab === 'erp-balance' && erpBalanceEnabled && <ImportErpBalanceTab customerId={customerId} />}

      {activeTab === 'erp-received' && isSuperuser && <ImportErpReceivedTab customerId={customerId} />}

      {activeTab === 'collab' && collabHoldsEnabled && <ImportCollabHoldsTab customerId={customerId} />}
      {activeTab === 'rules' && isSuperuser && <ImportRulesTab customerId={customerId} />}

      {activeTab === 'health' && (
        <ImportHealthCheckTab
          customerId={customerId}
          recentFailures={summary?.recent_failures ?? []}
          jobsFailedToday={summary?.jobs_failed_today ?? 0}
          recordsFailedToday={summary?.records_failed_today ?? 0}
          onSelectJob={setSelectedJobId}
        />
      )}
      </>)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat Cards
// ---------------------------------------------------------------------------

function StatCards({ summary, middleware }: { summary: DashboardSummary; middleware: MiddlewareStatusResponse | null }) {
  const pendingQueues = middleware?.queues?.filter(q => q.depth > 0) || [];
  const totalPending = pendingQueues.reduce((sum, q) => sum + q.depth, 0);

  const stats = [
    {
      label: 'Total Jobs',
      value: summary.total_jobs.toLocaleString(),
      sub: `${summary.jobs_today} today`,
      icon: Database,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      label: 'Running Now',
      value: summary.jobs_running.toString(),
      sub: summary.jobs_running > 0 ? 'In progress' : 'All clear',
      icon: Activity,
      color: summary.jobs_running > 0 ? 'text-emerald-600' : 'text-slate-500',
      bg: summary.jobs_running > 0 ? 'bg-emerald-50' : 'bg-slate-50',
    },
    {
      label: 'Queued',
      value: totalPending.toString(),
      sub: pendingQueues.length > 0 ? `${pendingQueues.length} queue${pendingQueues.length > 1 ? 's' : ''} pending` : 'All clear',
      icon: Clock,
      color: totalPending > 0 ? 'text-amber-600' : 'text-emerald-600',
      bg: totalPending > 0 ? 'bg-amber-50' : 'bg-emerald-50',
    },
    {
      label: 'Created Today',
      value: summary.records_created_today.toLocaleString(),
      sub: 'New records',
      icon: TrendingUp,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
    },
    {
      label: 'Updated Today',
      value: (summary.records_updated_today || 0).toLocaleString(),
      sub: 'Modified records',
      icon: TrendingUp,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      label: 'Failed Today',
      value: summary.jobs_failed_today.toString(),
      sub: `${summary.records_failed_today.toLocaleString()} records`,
      icon: XCircle,
      color: summary.jobs_failed_today > 0 ? 'text-red-600' : 'text-emerald-600',
      bg: summary.jobs_failed_today > 0 ? 'bg-red-50' : 'bg-emerald-50',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
      {stats.map(s => (
        <Card key={s.label} className="relative overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{s.label}</p>
                <p className="text-2xl font-bold mt-1">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>
              </div>
              <div className={`rounded-lg p-2.5 ${s.bg}`}>
                <s.icon className={`h-5 w-5 ${s.color}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data Type Chart
// ---------------------------------------------------------------------------

function DataTypeChart({ data }: { data: DashboardSummary['by_data_type'] }) {
  if (data.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">Import Volume by Type (7 days)</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
            No import data in the last 7 days
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map(d => ({
    name: d.data_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    succeeded: d.succeeded,
    failed: d.failed,
    total: d.total,
  }));

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Import Volume by Type (7 days)</CardTitle></CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" fontSize={12} />
            <YAxis type="category" dataKey="name" fontSize={11} width={120} />
            <Tooltip />
            <Bar dataKey="succeeded" stackId="a" fill="#10b981" name="Succeeded" radius={[0, 0, 0, 0]} />
            <Bar dataKey="failed" stackId="a" fill="#ef4444" name="Failed" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Middleware Status
// ---------------------------------------------------------------------------

function MiddlewareStatus({ data }: { data: MiddlewareStatusResponse | null }) {
  if (!data || data.error) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">Middleware Queues</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-amber-600 text-sm">
            <AlertTriangle className="h-4 w-4" />
            Redis unavailable — queue monitoring offline
          </div>
        </CardContent>
      </Card>
    );
  }

  const queues = data.queues;

  if (queues.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">Middleware Queues</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
            No active queue configurations
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalDepth = queues.reduce((sum, q) => sum + Math.max(q.depth, 0), 0);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base">Middleware Queues</CardTitle>
        <Badge variant={totalDepth > 0 ? 'destructive' : 'secondary'} className="text-xs">
          {totalDepth > 0 ? `${totalDepth} pending` : 'All clear'}
        </Badge>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {queues.map((q, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
              <div>
                <p className="text-sm font-medium">{q.customer}</p>
                <p className="text-xs text-muted-foreground">
                  {q.data_type.replace(/_/g, ' ')} &middot; {q.queue_key}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {q.depth > 0 ? (
                  <Badge variant="destructive" className="tabular-nums">{q.depth}</Badge>
                ) : q.depth === 0 ? (
                  <Badge variant="secondary" className="text-emerald-700 bg-emerald-50">0</Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600">err</Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Recent Failures
// ---------------------------------------------------------------------------

function RecentFailures({ failures }: { failures: ImportJob[] }) {
  if (failures.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">Recent Failures</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-48 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mb-2" />
            <p className="text-sm font-medium text-emerald-700">No recent failures</p>
            <p className="text-xs text-muted-foreground mt-1">All imports completed successfully</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Recent Failures</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-3">
          {failures.slice(0, 5).map(job => (
            <div key={job.id} className="flex items-start gap-3 py-2 border-b last:border-0">
              <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">
                  {job.data_type_display} &middot; {job.customer_name}
                </p>
                <p className="text-xs text-muted-foreground truncate">{job.error_message || 'No error message'}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {timeAgo(job.created_at)} &middot; {job.records_failed} failed of {job.total_records}
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Job Management
// ---------------------------------------------------------------------------

function JobManagementCard({ jobs, onRefresh }: { jobs: ImportJob[]; onRefresh?: () => void }) {
  const [cancelling, setCancelling] = useState<string | null>(null);

  const handleCancel = async (jobId: string) => {
    setCancelling(jobId);
    try {
      await importsAPI.cancelJob(jobId);
      toast.success('Job cancelled successfully');
      onRefresh?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to cancel job';
      toast.error(msg);
    } finally {
      setCancelling(null);
    }
  };

  const isStuck = (job: ImportJob) => {
    if (!job.started_at) return false;
    return (Date.now() - new Date(job.started_at).getTime()) / 60000 > 30;
  };

  const activeJobs = jobs.filter(j => j.status === 'running' || j.status === 'pending');
  const recentDone = jobs
    .filter(j => j.status !== 'running' && j.status !== 'pending')
    .slice(0, 8);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          Job Management
          {activeJobs.length > 0 && (
            <Badge className="bg-blue-100 text-blue-700 border-blue-200">
              {activeJobs.length} active
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Active / Running Jobs */}
        {activeJobs.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Running</p>
            {activeJobs.map(job => (
              <div key={job.id} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-blue-50 border border-blue-100">
                <div className="relative">
                  <Activity className={`h-4 w-4 ${isStuck(job) ? 'text-red-500' : 'text-blue-500'}`} />
                  <span className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ${isStuck(job) ? 'bg-red-500' : 'bg-blue-500'} animate-pulse`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {job.data_type_display}
                    {isStuck(job) && <span className="ml-2 text-xs text-red-600 font-normal">Stuck</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {job.customer_name} &middot; {timeAgo(job.started_at)}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs text-red-600 border-red-200 hover:bg-red-50 h-7"
                  disabled={cancelling === job.job_id}
                  onClick={(e) => { e.stopPropagation(); handleCancel(job.job_id); }}
                >
                  {cancelling === job.job_id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Cancel'}
                </Button>
              </div>
            ))}
          </div>
        )}

        {activeJobs.length === 0 && (
          <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-emerald-50 border border-emerald-100">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <p className="text-sm text-emerald-700">No active jobs — all clear</p>
          </div>
        )}

        {/* Recent Jobs */}
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Recent</p>
          {recentDone.map(job => (
            <div key={job.id} className="flex items-center gap-3 py-1.5 border-b last:border-0">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium">{job.data_type_display}</p>
                <p className="text-xs text-muted-foreground">{timeAgo(job.created_at)}</p>
              </div>
              <StatusBadge status={job.status} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

